// ModalPagoCafeteria.tsx — Co Marca — Modal de pago para ventas de cafetería
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
    Banknote, CreditCard, X, Smartphone, Wallet,
    AlertCircle, SplitSquareHorizontal,
} from 'lucide-react';
import { TipoPago } from '../../../services/tiposPagoService';

// ── Design tokens ────────────────────────────────────────────
const C = {
    primary: '#c1348b',
    primaryDark: '#8f1f64',
    secondary: '#61257b',
    secondaryDark: '#004d7a',
    secondaryLight: '#8b3fa8',
    accentDark: '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
.pm-wrap * { font-family:'DM Sans',sans-serif !important; box-sizing:border-box; }
@keyframes pmFadeUp  { from{opacity:0;transform:translateY(16px) scale(.98);}to{opacity:1;transform:translateY(0) scale(1);} }
@keyframes pmSpin    { to{transform:rotate(360deg);} }
.pm-modal { animation:pmFadeUp .32s cubic-bezier(.16,1,.3,1) both; }
.pm-spin   { animation:pmSpin .7s linear infinite; }
.pm-input { transition:border-color .2s, box-shadow .2s; }
.pm-input:focus { outline:none; border-color:${C.secondary} !important; box-shadow:0 0 0 3px ${C.secondary}18 !important; }
.pm-input:read-only { background:#f8fafc !important; }
.pm-input:disabled { opacity:.55; cursor:not-allowed; }
.pm-scroll::-webkit-scrollbar{width:5px;}
.pm-scroll::-webkit-scrollbar-track{background:transparent;}
.pm-scroll::-webkit-scrollbar-thumb{background:${C.secondary}30;border-radius:10px;}
.pm-btn-ghost { transition:all .18s; }
.pm-btn-ghost:hover { background:#f1f5f9 !important; }
.pm-btn-primary { transition:all .22s cubic-bezier(.16,1,.3,1); }
.pm-btn-primary:hover:not(:disabled) { transform:translateY(-2px); box-shadow:0 8px 24px ${C.secondary}35 !important; }
.pm-btn-primary:disabled { opacity:.5; cursor:not-allowed; transform:none !important; box-shadow:none !important; }
`;

export interface DatosPago {
    tipo_pago_id: number;
    monto: number;
    efectivo_recibido?: number;
}

interface ModalPagoCafeteriaProps {
    isOpen: boolean;
    onClose: () => void;
    totalVenta: number;       // monto total a cobrar
    tiposPago: TipoPago[];    // ya filtrados por POS
    onConfirmPago: (datos: DatosPago) => void;
}

const ModalPagoCafeteria: React.FC<ModalPagoCafeteriaProps> = ({
    isOpen, onClose, totalVenta, tiposPago, onConfirmPago,
}) => {
    const [tipoPagoId, setTipoPagoId] = useState<number | ''>('');
    const [tipoPagoSeleccionado, setTipoPagoSeleccionado] = useState<TipoPago | null>(null);
    const [montoAPagar, setMontoAPagar] = useState<number>(totalVenta);
    const [efectivoRecibido, setEfectivoRecibido] = useState<number>(totalVenta);
    const [cambio, setCambio] = useState<number>(0);
    const [error, setError] = useState<string | null>(null);

    const redondear = (valor: number) => Math.round((valor + Number.EPSILON) * 100) / 100;

    // Inicializar tipo de pago por defecto
    useEffect(() => {
        if (isOpen && tiposPago.length > 0) {
            const primerTipo = tiposPago[0];
            setTipoPagoId(primerTipo.id);
            setTipoPagoSeleccionado(primerTipo);
            setMontoAPagar(totalVenta);
            setEfectivoRecibido(totalVenta);
        } else {
            setTipoPagoId('');
            setTipoPagoSeleccionado(null);
        }
        setError(null);
    }, [isOpen, tiposPago, totalVenta]);

    // Recalcular cambio
    useEffect(() => {
        const config = tipoPagoSeleccionado?.configuracion_detail;
        if (!config) {
            setCambio(0);
            return;
        }
        if (config.requiere_cambio) {
            setCambio(redondear(efectivoRecibido > montoAPagar ? efectivoRecibido - montoAPagar : 0));
        } else if (config.permite_monto_personalizado && !config.afecta_caja && efectivoRecibido > montoAPagar) {
            setCambio(redondear(efectivoRecibido - montoAPagar));
        } else {
            setCambio(0);
            if (!config.afecta_caja && !config.permite_monto_personalizado) {
                setEfectivoRecibido(redondear(montoAPagar));
            }
        }
    }, [efectivoRecibido, montoAPagar, tipoPagoSeleccionado]);

    const handleTipoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const id = Number(e.target.value);
        setTipoPagoId(id);
        const tipo = tiposPago.find(t => t.id === id) || null;
        setTipoPagoSeleccionado(tipo);
        setMontoAPagar(totalVenta);
        setEfectivoRecibido(totalVenta);
        setError(null);
    };

    const getIconoTipo = (tipo: string) => {
        switch (tipo) {
            case 'efectivo': return <Banknote size={16} />;
            case 'banco': return <CreditCard size={16} />;
            case 'otro': return <Smartphone size={16} />;
            default: return <Wallet size={16} />;
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        if (!tipoPagoId || !tipoPagoSeleccionado) { setError('Seleccione un tipo de pago'); return; }
        const config = tipoPagoSeleccionado.configuracion_detail;
        const montoFinal = redondear(montoAPagar);
        const efectivoRedondeado = redondear(efectivoRecibido);

        if (config) {
            if (config.requiere_cambio && efectivoRedondeado <= 0) { setError('El efectivo recibido debe ser mayor a 0'); return; }
            if (config.requiere_cambio && efectivoRedondeado < montoFinal) { setError('El efectivo recibido no puede ser menor al monto'); return; }
        }
        if (montoFinal <= 0) { setError('Monto inválido'); return; }

        const datos: DatosPago = {
            tipo_pago_id: tipoPagoId as number,
            monto: montoFinal,
        };
        if (config?.requiere_cambio || (config?.permite_monto_personalizado && !config?.afecta_caja && efectivoRedondeado > montoFinal)) {
            datos.efectivo_recibido = efectivoRedondeado;
        } else if (config?.afecta_caja) {
            datos.efectivo_recibido = montoFinal;
        }
        onConfirmPago(datos);
    };

    if (!isOpen) return null;

    const config = tipoPagoSeleccionado?.configuracion_detail;
    const permitePersonalizado = config?.permite_monto_personalizado ?? false;
    const esPagoDigitalConCambio = permitePersonalizado && !config?.afecta_caja && efectivoRecibido > montoAPagar;

    const inputBase: React.CSSProperties = {
        width: '100%', padding: '10px 14px', borderRadius: '10px',
        border: '1.5px solid #e2e8f0', fontSize: '.88rem', color: '#0f172a',
        background: '#fff', fontFamily: "'DM Sans',sans-serif", fontWeight: '500',
    };

    const configColor = (afecta: boolean, requiere: boolean) => {
        if (!afecta) return { bg: '#7c3aed', light: '#ede9fe', border: '#c4b5fd' };
        if (afecta && requiere) return { bg: C.secondary, light: `${C.secondary}12`, border: `${C.secondary}30` };
        return { bg: C.secondaryLight, light: `${C.secondaryLight}12`, border: `${C.secondaryLight}30` };
    };

    return ReactDOM.createPortal(
        <div className="pm-wrap" style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
            <style>{css}</style>
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', backdropFilter: 'blur(4px)' }} onClick={onClose} />
            <div className="pm-modal" style={{ position: 'relative', background: '#fff', borderRadius: '22px', maxWidth: '520px', width: '100%', boxShadow: '0 24px 80px rgba(0,0,0,.18)', overflow: 'hidden', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
                <div style={{ height: '4px', background: `linear-gradient(90deg,${C.secondary},${C.secondaryLight},${C.accentDark},${C.primary})`, flexShrink: 0 }} />

                {/* HEADER */}
                <div style={{ padding: '22px 26px 18px', borderBottom: '1.5px solid #f1f5f9', flexShrink: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '11px', background: `linear-gradient(135deg,${C.secondary},${C.secondaryDark})`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 3px 12px ${C.secondary}30` }}>
                            <CreditCard size={17} color="#fff" />
                        </div>
                        <div>
                            <h3 style={{ fontSize: '1rem', fontWeight: '900', color: '#0f172a' }}>Registrar Pago</h3>
                            <p style={{ fontSize: '.75rem', color: '#94a3b8' }}>Complete la información del pago</p>
                        </div>
                    </div>
                    <button onClick={onClose} style={{ width: '32px', height: '32px', borderRadius: '9px', border: '1.5px solid #f1f5f9', background: '#f8fafc', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <X size={15} color="#64748b" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                    <div className="pm-scroll" style={{ padding: '22px 26px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {/* Monto total */}
                        <div style={{ background: `${C.secondary}08`, border: `1.5px solid ${C.secondary}20`, borderRadius: '14px', padding: '16px', textAlign: 'center' }}>
                            <p style={{ fontSize: '.75rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>Total a cobrar</p>
                            <p style={{ fontSize: '1.6rem', fontWeight: '900', color: C.secondaryDark }}>{totalVenta.toFixed(2)} <span style={{ fontSize: '.9rem', color: '#94a3b8' }}>Bs.</span></p>
                        </div>

                        {/* Error */}
                        {error && (
                            <div style={{ background: `${C.primary}07`, borderLeft: `4px solid ${C.primary}`, borderRadius: '0 12px 12px 0', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <AlertCircle size={15} color={C.primary} />
                                <p style={{ fontSize: '.83rem', fontWeight: '600', color: C.primaryDark }}>{error}</p>
                            </div>
                        )}

                        {/* Tipo de pago */}
                        <div>
                            <label style={{ display: 'block', fontSize: '.8rem', fontWeight: '700', color: '#374151', marginBottom: '8px' }}>Tipo de Pago <span style={{ color: C.primary }}>*</span></label>
                            <select value={tipoPagoId} onChange={handleTipoChange}
                                className="pm-input"
                                style={{ ...inputBase, appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center', paddingRight: '36px' }}>
                                {tiposPago.map(tipo => <option key={tipo.id} value={tipo.id}>{tipo.nombre}</option>)}
                            </select>

                            {/* Badge config */}
                            {config && (() => {
                                const cc = configColor(config.afecta_caja, config.requiere_cambio);
                                return (
                                    <div style={{ marginTop: '12px', background: cc.light, border: `1.5px solid ${cc.border}`, borderRadius: '14px', padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                        <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: cc.light, border: `1.5px solid ${cc.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: cc.bg, flexShrink: 0 }}>
                                            {getIconoTipo(config.tipo)}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <p style={{ fontSize: '.85rem', fontWeight: '800', color: '#0f172a', marginBottom: '8px' }}>{config.nombre}</p>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', background: config.afecta_caja ? `${C.secondaryLight}20` : '#ede9fe', border: `1px solid ${config.afecta_caja ? C.secondaryLight : '#c4b5fd'}`, borderRadius: '100px', padding: '3px 9px', fontSize: '.65rem', fontWeight: '700', color: config.afecta_caja ? '#065f46' : '#6d28d9' }}>
                                                    {config.afecta_caja ? 'Afecta a caja' : 'No afecta a caja'}
                                                </span>
                                                {config.requiere_cambio && <span style={{ display: 'inline-flex', alignItems: 'center', background: `${C.secondary}15`, border: `1px solid ${C.secondary}30`, borderRadius: '100px', padding: '3px 9px', fontSize: '.65rem', fontWeight: '700', color: C.secondaryDark }}>Requiere cambio</span>}
                                                {config.permite_monto_personalizado && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', background: '#ede9fe', border: '1px solid #c4b5fd', borderRadius: '100px', padding: '3px 9px', fontSize: '.65rem', fontWeight: '700', color: '#6d28d9' }}><SplitSquareHorizontal size={10} /> Pago parcial</span>}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>

                        {/* Monto a pagar */}
                        <div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '.8rem', fontWeight: '700', color: '#374151', marginBottom: '8px' }}>
                                Monto a Pagar <span style={{ color: C.primary }}>*</span>
                                {permitePersonalizado && <span style={{ fontSize: '.65rem', fontWeight: '700', background: '#ede9fe', color: '#6d28d9', borderRadius: '100px', padding: '2px 8px' }}>Monto libre</span>}
                            </label>
                            <input type="number" min="0.01" step="0.01" max={totalVenta}
                                value={montoAPagar}
                                onChange={e => {
                                    if (permitePersonalizado) {
                                        const val = parseFloat(e.target.value);
                                        const nm = isNaN(val) ? 0 : redondear(Math.min(val, totalVenta));
                                        setMontoAPagar(nm);
                                        if (!config?.requiere_cambio && config?.afecta_caja) setEfectivoRecibido(nm);
                                    }
                                }}
                                readOnly={!permitePersonalizado}
                                className="pm-input"
                                style={{ ...inputBase, borderColor: permitePersonalizado ? '#c4b5fd' : '#e2e8f0', background: permitePersonalizado ? '#fff' : '#f8fafc', fontWeight: '700', fontSize: '1rem' }}
                            />
                            {permitePersonalizado && <p style={{ fontSize: '.72rem', color: '#7c3aed', fontWeight: '500', marginTop: '5px' }}>Puede ingresar un monto parcial (máx: {totalVenta.toFixed(2)} Bs.)</p>}
                        </div>

                        {/* Efectivo recibido */}
                        {(config?.afecta_caja || esPagoDigitalConCambio || (permitePersonalizado && !config?.afecta_caja)) && (
                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '.8rem', fontWeight: '700', color: '#374151', marginBottom: '8px' }}>
                                    {config?.requiere_cambio || esPagoDigitalConCambio ? 'Monto Recibido *' : 'Monto a Pagar'}
                                    {!config?.afecta_caja && permitePersonalizado && <span style={{ fontSize: '.65rem', fontWeight: '700', background: '#ede9fe', color: '#6d28d9', borderRadius: '100px', padding: '2px 8px' }}>QR / Digital</span>}
                                </label>
                                <input type="number" min="0" step="0.01" value={efectivoRecibido}
                                    onChange={e => { const v = parseFloat(e.target.value); setEfectivoRecibido(isNaN(v) ? 0 : redondear(v)); }}
                                    className="pm-input"
                                    style={{ ...inputBase, fontWeight: '700', fontSize: '1rem' }}
                                    disabled={(!config?.requiere_cambio && !(permitePersonalizado && !config?.afecta_caja))}
                                    readOnly={(!config?.requiere_cambio && !(permitePersonalizado && !config?.afecta_caja))}
                                />
                                <p style={{ fontSize: '.72rem', color: '#94a3b8', fontWeight: '500', marginTop: '5px' }}>
                                    {config?.requiere_cambio ? 'Ingrese el monto recibido para calcular el cambio'
                                        : permitePersonalizado && !config?.afecta_caja ? 'Si recibe más del monto, el cambio se registrará como salida de caja en efectivo'
                                            : 'Pago exacto — no requiere cambio'}
                                </p>
                            </div>
                        )}

                        {/* Cambio */}
                        {(config?.requiere_cambio || esPagoDigitalConCambio) && (
                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '.8rem', fontWeight: '700', color: '#374151', marginBottom: '8px' }}>
                                    Cambio a Entregar
                                </label>
                                <div style={{ ...inputBase, background: cambio > 0 ? `${C.secondaryLight}10` : '#f8fafc', border: `1.5px solid ${cambio > 0 ? C.secondaryLight : '#e2e8f0'}`, color: cambio > 0 ? '#065f46' : '#0f172a', fontWeight: '800', fontSize: '1.05rem', borderRadius: '10px' }}>
                                    {cambio.toFixed(2)} <span style={{ fontSize: '.75rem', fontWeight: '500', color: '#94a3b8' }}>Bs.</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div style={{ padding: '18px 26px', borderTop: '1.5px solid #f1f5f9', background: '#fafafa', flexShrink: 0, display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <button type="button" onClick={onClose} className="pm-btn-ghost"
                            style={{ padding: '10px 20px', borderRadius: '11px', border: '1.5px solid #e2e8f0', background: '#fff', fontSize: '.85rem', fontWeight: '700', color: '#64748b', cursor: 'pointer' }}>
                            Cancelar
                        </button>
                        <button type="submit" disabled={!tipoPagoId || !config} className="pm-btn-primary"
                            style={{ padding: '10px 24px', borderRadius: '11px', border: 'none', background: (tipoPagoId && config) ? `linear-gradient(135deg,${C.secondary},${C.secondaryDark})` : '#e2e8f0', color: (tipoPagoId && config) ? '#fff' : '#94a3b8', fontSize: '.88rem', fontWeight: '800', cursor: (tipoPagoId && config) ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: '8px', minWidth: '140px', justifyContent: 'center' }}>
                            <CreditCard size={15} /> Registrar Pago
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
};

export default ModalPagoCafeteria;